import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, AlertCircle, Factory, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = '';

const MAIN_STAGES = [
  { key: 'cutting', name: 'Cutting' },
  { key: 'closing', name: 'Closing' },
  { key: 'lasting', name: 'Lasting' },
  { key: 'pasting', name: 'Pasting' },
  { key: 'finishing', name: 'Finishing' },
  { key: 'packing', name: 'Packing' },
  { key: 'dispatch', name: 'Dispatch' },
];

const PREP_SUB_STAGES = [
  { key: 'prep_printing', name: 'Printing' },
  { key: 'prep_skiving', name: 'Skiving' },
  { key: 'prep_marking', name: 'Marking' },
  { key: 'prep_crimping', name: 'Crimping' },
  { key: 'prep_nailing', name: 'Nailing' },
  { key: 'prep_socks', name: 'Socks' },
];

const MAIN_STAGE_KEYS = MAIN_STAGES.map(s => s.key);

export default function Production() {
  const { user } = useAuth();
  const canRecover = user?.role === 'admin' || user?.role === 'manager';
  const [pos, setPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState('');
  const [poData, setPOData] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [stageConfigs, setStageConfigs] = useState([]);
  const [entryModal, setEntryModal] = useState(false);
  const [batchForm, setBatchForm] = useState({ job_card_number: '', stage: '', job_worker_id: '', date: new Date(), items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shortages, setShortages] = useState([]); // [{entry_id, sku, size, stage, stage_name, loss_qty, ...}]
  const [recovering, setRecovering] = useState(null); // {entry_id, max_qty}
  const [recoverQty, setRecoverQty] = useState('');

  const fetchPOs = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/po?limit=500`, { withCredentials: true }); setPOs(data.pos || []); } catch {}
  }, []);
  const fetchWorkers = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/job-workers`, { withCredentials: true }); setWorkers(data); } catch {}
  }, []);
  const fetchStageConfigs = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/stage-config`, { withCredentials: true }); setStageConfigs(data); } catch {}
  }, []);
  const fetchPOProduction = useCallback(async (poNum) => {
    if (!poNum) { setPOData(null); return; }
    try { const { data } = await axios.get(`${API}/api/production/po?po_number=${encodeURIComponent(poNum)}`, { withCredentials: true }); setPOData(data); } catch { setPOData(null); }
  }, []);

  useEffect(() => { fetchPOs(); fetchWorkers(); fetchStageConfigs(); }, [fetchPOs, fetchWorkers, fetchStageConfigs]);

  const handlePOSelect = (poNum) => { setSelectedPO(poNum); fetchPOProduction(poNum); };

  const openBatchEntry = (jcNumber, stage, jcItems) => {
    setError('');
    const isPrep = stage.startsWith('prep_');

    const items = jcItems.map(item => {
      let prevOutput = '';
      const poItem = poData?.items?.find(pi => pi.job_card_number === jcNumber && pi.sku === item.sku && pi.size === item.size);

      if (stage === 'cutting') {
        prevOutput = item.planned_qty;
      } else if (isPrep || stage === 'closing') {
        const cuttingEntry = poItem?.main_stages?.find(s => s.stage === 'cutting');
        if (cuttingEntry && cuttingEntry.status !== 'pending') prevOutput = cuttingEntry.output_qty;
      } else if (poItem) {
        const mainIdx = MAIN_STAGES.findIndex(s => s.key === stage);
        if (mainIdx > 0) {
          const prevMainStage = MAIN_STAGES[mainIdx - 1].key;
          const prevEntry = poItem.main_stages?.find(s => s.stage === prevMainStage);
          if (prevEntry && prevEntry.status !== 'pending') prevOutput = prevEntry.output_qty;
        }
      }
      return { sku: item.sku, size: item.size, input_qty: prevOutput !== '' ? String(prevOutput) : '', output_qty: '' };
    });

    // For prep stages, calculate totals
    const totalInput = items.reduce((s, i) => s + (parseInt(i.input_qty) || 0), 0);

    setBatchForm({
      job_card_number: jcNumber, stage, job_worker_id: '', date: new Date(), items,
      isPrep,
      totalInput: String(totalInput),
      totalOutput: '',
    });
    setShortages([]);
    // Fetch shortages from earlier stages so user can recover them inline
    axios.get(`${API}/api/production/shortages?job_card_number=${encodeURIComponent(jcNumber)}&stage=${stage}`, { withCredentials: true })
      .then(({ data }) => setShortages(data.shortages || []))
      .catch(() => setShortages([]));
    setEntryModal(true);
  };

  const submitRecover = async () => {
    if (!recovering) return;
    const qty = parseInt(recoverQty);
    if (!qty || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    if (qty > recovering.max_qty) { toast.error(`Max recoverable: ${recovering.max_qty}`); return; }
    try {
      const { data } = await axios.post(
        `${API}/api/production/recover-shortage?entry_id=${recovering.entry_id}&recover_qty=${qty}`,
        {}, { withCredentials: true }
      );
      toast.success(data.message || 'Recovered');
      // Refresh shortages list
      const { data: sd } = await axios.get(`${API}/api/production/shortages?job_card_number=${encodeURIComponent(batchForm.job_card_number)}&stage=${batchForm.stage}`, { withCredentials: true });
      setShortages(sd.shortages || []);
      // Refresh PO production grid in background
      if (selectedPO) fetchPOProduction(selectedPO);
      // Warn about later stages that need top-up
      if (data.later_stages_needing_topup?.length) {
        toast.info(`Note: ${data.later_stages_needing_topup.length} later stage(s) may need re-entry.`);
      }
      setRecovering(null); setRecoverQty('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to recover');
    }
  };

  const updateBatchItem = (idx, field, value) => {
    setBatchForm(prev => { const items = [...prev.items]; items[idx] = { ...items[idx], [field]: value }; return { ...prev, items }; });
  };

  const submitBatchEntry = async () => {
    setError('');
    if (!batchForm.job_worker_id) { setError('Select a worker'); return; }

    let itemsToSubmit;

    if (batchForm.isPrep) {
      // For prep stages — use total qty and distribute across SKUs proportionally
      const totalIn = parseInt(batchForm.totalInput) || 0;
      const totalOut = parseInt(batchForm.totalOutput) || 0;
      if (!totalIn || !batchForm.totalOutput) { setError('Enter total output qty'); return; }
      if (totalOut > totalIn) { setError('Output cannot exceed input'); return; }

      // Distribute proportionally based on each SKU's cutting output (input_qty)
      const skuInputs = batchForm.items.map(i => parseInt(i.input_qty) || 0);
      const sumInputs = skuInputs.reduce((a, b) => a + b, 0);

      if (sumInputs === 0) { setError('No cutting data found'); return; }

      let distributedOut = 0;
      itemsToSubmit = batchForm.items.map((item, idx) => {
        const skuIn = parseInt(item.input_qty) || 0;
        let skuOut;
        if (idx === batchForm.items.length - 1) {
          skuOut = totalOut - distributedOut; // last item gets remainder
        } else {
          skuOut = Math.round((skuIn / sumInputs) * totalOut);
          distributedOut += skuOut;
        }
        return { sku: item.sku, size: item.size, input_qty: skuIn, output_qty: Math.min(skuOut, skuIn) };
      });
    } else {
      // For main stages — per SKU entry
      for (const item of batchForm.items) {
        if (!item.input_qty || !item.output_qty) { setError('Enter qty for all SKUs'); return; }
        if (parseInt(item.output_qty) > parseInt(item.input_qty)) { setError(`Output > input for ${item.sku}`); return; }
      }
      itemsToSubmit = batchForm.items.map(i => ({ sku: i.sku, size: i.size, input_qty: parseInt(i.input_qty), output_qty: parseInt(i.output_qty) }));
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/production/batch-entry`, {
        job_card_number: batchForm.job_card_number, stage: batchForm.stage,
        job_worker_id: batchForm.job_worker_id, date: format(batchForm.date, 'yyyy-MM-dd'),
        items: itemsToSubmit
      }, { withCredentials: true });
      const stageName = [...MAIN_STAGES, ...PREP_SUB_STAGES].find(s => s.key === batchForm.stage)?.name || batchForm.stage;
      toast.success(`${stageName} saved for ${batchForm.job_card_number}`);
      setEntryModal(false);
      fetchPOProduction(selectedPO);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const selectedPOObj = pos.find(p => p.po_number === selectedPO);

  // Group poData items by job_card_number
  const groupedByJC = {};
  if (poData?.items) {
    for (const item of poData.items) {
      const jc = item.job_card_number || 'legacy';
      if (!groupedByJC[jc]) { groupedByJC[jc] = { job_card_number: item.job_card_number, items: [], mainStages: {}, prepStages: {} }; }
      groupedByJC[jc].items.push(item);

      for (const s of (item.main_stages || [])) {
        if (!groupedByJC[jc].mainStages[s.stage]) groupedByJC[jc].mainStages[s.stage] = { totalIn: 0, totalOut: 0, totalLoss: 0, worker: '', count: 0, pending: 0 };
        const st = groupedByJC[jc].mainStages[s.stage];
        if (s.status === 'pending') st.pending++; else { st.totalIn += s.input_qty || 0; st.totalOut += s.output_qty || 0; st.totalLoss += s.loss_qty || 0; st.worker = s.job_worker_name || ''; st.count++; }
      }
      for (const s of (item.prep_stages || [])) {
        if (!groupedByJC[jc].prepStages[s.stage]) groupedByJC[jc].prepStages[s.stage] = { totalIn: 0, totalOut: 0, totalLoss: 0, worker: '', count: 0, pending: 0 };
        const st = groupedByJC[jc].prepStages[s.stage];
        if (s.status === 'pending') st.pending++; else { st.totalIn += s.input_qty || 0; st.totalOut += s.output_qty || 0; st.totalLoss += s.loss_qty || 0; st.worker = s.job_worker_name || ''; st.count++; }
      }
    }
  }

  const renderStageCell = (stageData, stageKey, jcNum, jcData, isPrep) => {
    const allPending = !stageData || stageData.pending === jcData.items.length;
    const allDone = stageData && stageData.count === jcData.items.length;

    if (allPending) {
      // Check prerequisite: cutting must be done for prep and closing
      const cuttingDone = jcData.mainStages['cutting']?.count === jcData.items.length;
      let canAdd = false;
      if (stageKey === 'cutting') canAdd = true;
      else if (isPrep) canAdd = cuttingDone;
      else if (stageKey === 'closing') canAdd = cuttingDone;
      else {
        const mainIdx = MAIN_STAGES.findIndex(s => s.key === stageKey);
        if (mainIdx > 0) canAdd = jcData.mainStages[MAIN_STAGES[mainIdx - 1].key]?.count === jcData.items.length;
      }

      return jcData.job_card_number && canAdd ? (
        <button onClick={() => openBatchEntry(jcData.job_card_number, stageKey, jcData.items)}
          className="w-full p-1.5 border border-dashed border-zinc-300 rounded-sm hover:border-zinc-400 hover:bg-zinc-50 transition-colors text-zinc-400 text-[10px]"
          data-testid={`add-${stageKey}-${jcNum}`}>+ Add</button>
      ) : <span className="text-[10px] text-zinc-300">—</span>;
    }

    const lossPercent = stageData.totalIn > 0 ? ((stageData.totalLoss / stageData.totalIn) * 100).toFixed(1) : 0;
    const cfg = stageConfigs.find(c => c.stage === stageKey);
    const threshold = cfg?.loss_threshold_percent || 2;
    let cellColor = 'bg-green-50 text-green-700 border-green-200';
    if (stageData.totalLoss > 0) cellColor = parseFloat(lossPercent) > threshold ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200';

    return (
      <div className={`p-1.5 rounded-sm border text-[10px] ${cellColor} cursor-pointer hover:opacity-80`}
        onClick={() => allDone && openBatchEntry(jcData.job_card_number, stageKey, jcData.items)}
        data-testid={`stage-${stageKey}-${jcNum}`}>
        <div className="font-bold">{stageData.totalIn}&rarr;{stageData.totalOut}</div>
        {stageData.totalLoss > 0 && <div className="flex items-center justify-center gap-0.5"><AlertTriangle size={8} /><span>-{stageData.totalLoss}</span></div>}
        <div className="text-[8px] opacity-70 truncate">{stageData.worker}</div>
        {!allDone && <div className="text-[8px] text-amber-600">{stageData.count}/{jcData.items.length}</div>}
      </div>
    );
  };

  return (
    <div className="p-6" data-testid="production-page">
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Production Tracking</h1>
        <p className="text-sm text-zinc-500">Track compulsory stages + optional preparation sub-stages per Job Card</p>
      </div>

      {/* PO Selector */}
      <div className="bg-white border border-zinc-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Select PO</Label>
            <Select value={selectedPO || 'none'} onValueChange={v => handlePOSelect(v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="production-po-select"><SelectValue placeholder="Choose a PO" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Choose a PO</SelectItem>
                {pos.map(po => (<SelectItem key={po.id} value={po.po_number}><span className="font-mono">{po.po_number}</span> - {po.buyer_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {selectedPOObj && (<>
            <div className="text-sm"><span className="text-zinc-500">Buyer:</span> <span className="ml-2 font-medium">{selectedPOObj.buyer_name}</span></div>
            <div className="text-sm"><span className="text-zinc-500">SKUs:</span> <span className="ml-2 font-mono font-medium">{selectedPOObj.line_items?.length || 0}</span></div>
          </>)}
        </div>
      </div>

      {/* Stage Legend */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1 items-center mb-1">
          <span className="text-[10px] text-zinc-500 font-semibold mr-1">COMPULSORY:</span>
          {MAIN_STAGES.map((s, i) => (
            <div key={s.key} className="flex items-center gap-0.5">
              <span className="text-[10px] font-medium text-zinc-700 bg-zinc-100 px-1.5 py-0.5 rounded-sm">{s.name}</span>
              {i < MAIN_STAGES.length - 1 && <ChevronRight size={10} className="text-zinc-300" />}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1 items-center">
          <span className="text-[10px] text-zinc-500 font-semibold mr-1">PREP (Optional):</span>
          {PREP_SUB_STAGES.map(s => (
            <span key={s.key} className="text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-sm">{s.name}</span>
          ))}
        </div>
      </div>

      {/* Production Grid */}
      {selectedPO && poData && (
        <div className="bg-white border border-zinc-200">
          <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
            <Factory size={18} className="text-zinc-500" />
            <h2 className="font-heading font-bold text-lg">Job Card Production</h2>
            <span className="text-xs text-zinc-400 ml-2">({Object.keys(groupedByJC).length} job cards)</span>
          </div>

          {Object.keys(groupedByJC).length === 0 ? (
            <div className="p-8 text-center text-zinc-500">No job cards for this PO. Create one first.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1200px]" data-testid="production-grid">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold sticky left-0 bg-zinc-50 z-10" rowSpan={2}>Job Card</th>
                    <th className="text-center p-1 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-100" colSpan={1}>Cut</th>
                    <th className="text-center p-1 text-[10px] uppercase tracking-wider text-purple-500 font-semibold bg-purple-50" colSpan={PREP_SUB_STAGES.length}>Preparation (Optional)</th>
                    {MAIN_STAGES.slice(1).map(s => (
                      <th key={s.key} className="text-center p-1 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold" colSpan={1}>{s.name}</th>
                    ))}
                  </tr>
                  <tr>
                    <th className="text-center p-1 text-[9px] text-zinc-400 bg-zinc-100">Cutting</th>
                    {PREP_SUB_STAGES.map(s => (
                      <th key={s.key} className="text-center p-1 text-[9px] text-purple-400 bg-purple-50/50">{s.name}</th>
                    ))}
                    {MAIN_STAGES.slice(1).map(s => (
                      <th key={s.key} className="text-center p-1 text-[9px] text-zinc-400">{''}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedByJC).map(([jcNum, jcData], idx) => (
                    <tr key={jcNum} className={`border-t border-zinc-200 ${idx % 2 === 1 ? 'bg-zinc-50/30' : ''}`} data-testid={`prod-row-${jcNum}`}>
                      <td className="p-2 sticky left-0 bg-white z-10">
                        <div className="font-mono text-xs font-bold text-[#09090B]">{jcData.job_card_number || 'Legacy'}</div>
                        <div className="text-[9px] text-zinc-400 mt-0.5 max-w-[120px] truncate">{jcData.items.length} SKUs</div>
                      </td>
                      {/* Cutting */}
                      <td className="p-1 text-center">{renderStageCell(jcData.mainStages['cutting'], 'cutting', jcNum, jcData, false)}</td>
                      {/* Prep sub-stages */}
                      {PREP_SUB_STAGES.map(s => (
                        <td key={s.key} className="p-1 text-center bg-purple-50/20">{renderStageCell(jcData.prepStages[s.key], s.key, jcNum, jcData, true)}</td>
                      ))}
                      {/* Remaining main stages */}
                      {MAIN_STAGES.slice(1).map(s => (
                        <td key={s.key} className="p-1 text-center">{renderStageCell(jcData.mainStages[s.key], s.key, jcNum, jcData, false)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Batch Entry Modal */}
      <Dialog open={entryModal} onOpenChange={setEntryModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="production-entry-dialog">
          <DialogHeader>
            <DialogTitle>
              {batchForm.job_card_number} — {[...MAIN_STAGES, ...PREP_SUB_STAGES].find(s => s.key === batchForm.stage)?.name}
              {batchForm.stage.startsWith('prep_') && <span className="text-purple-500 text-sm ml-2">(Optional)</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm"><AlertCircle size={14} /> {error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Worker *</Label>
                <Select value={batchForm.job_worker_id || 'none'} onValueChange={v => setBatchForm(p => ({ ...p, job_worker_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="mt-1 h-9 rounded-sm text-sm" data-testid="entry-worker-select"><SelectValue placeholder="Select worker" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select worker</SelectItem>
                    {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 h-9 rounded-sm text-sm justify-start">
                      <CalendarIcon size={12} className="mr-2" />{format(batchForm.date, 'yyyy-MM-dd')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={batchForm.date} onSelect={d => d && setBatchForm(p => ({ ...p, date: d }))} /></PopoverContent>
                </Popover>
              </div>
            </div>

            {/* For prep stages: simple total qty entry */}
            {batchForm.isPrep ? (
              <div className="space-y-3">
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-sm text-xs text-purple-700">
                  Total qty entry — applies across all {batchForm.items.length} SKUs (distributed proportionally)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total Input (= Cutting Output)</Label>
                    <Input type="number" value={batchForm.totalInput} readOnly className="mt-1 h-9 rounded-sm text-sm font-mono bg-zinc-50" data-testid="prep-total-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total Output *</Label>
                    <Input type="number" value={batchForm.totalOutput} onChange={e => setBatchForm(p => ({ ...p, totalOutput: e.target.value }))} className="mt-1 h-9 rounded-sm text-sm font-mono" data-testid="prep-total-output" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Loss</Label>
                    <div className={`mt-1 h-9 flex items-center px-3 rounded-sm text-sm font-mono font-bold ${
                      (parseInt(batchForm.totalInput) || 0) - (parseInt(batchForm.totalOutput) || 0) > 0 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
                    }`}>
                      {batchForm.totalOutput ? `${(parseInt(batchForm.totalInput) || 0) - (parseInt(batchForm.totalOutput) || 0)}` : '—'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* For main stages: per-SKU entry */
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">SKU Quantities</Label>
                <table className="w-full text-sm border border-zinc-200">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left p-2 text-[10px] uppercase text-zinc-500">SKU</th>
                      <th className="text-center p-2 text-[10px] uppercase text-zinc-500">Size</th>
                      <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Input</th>
                      <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Output</th>
                      <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchForm.items.map((item, idx) => {
                      const loss = (parseInt(item.input_qty) || 0) - (parseInt(item.output_qty) || 0);
                      const skuShortages = shortages.filter(s => s.sku === item.sku && String(s.size) === String(item.size));
                      const totalShort = skuShortages.reduce((sum, s) => sum + (s.loss_qty || 0), 0);
                      return (
                        <tr key={`${item.sku}-${item.size}`} className="border-t border-zinc-200">
                          <td className="p-2 font-mono text-xs font-medium">
                            <div>{item.sku}</div>
                            {totalShort > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1" data-testid={`shortage-${item.sku}-${item.size}`}>
                                {skuShortages.map(sh => (
                                  <button
                                    key={sh.entry_id}
                                    type="button"
                                    onClick={() => canRecover ? (setRecovering({ entry_id: sh.entry_id, max_qty: sh.loss_qty, sku: sh.sku, size: sh.size, stage_name: sh.stage_name }), setRecoverQty('')) : null}
                                    className={`text-[10px] px-1.5 py-0.5 rounded-sm border font-mono ${canRecover ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 cursor-pointer' : 'bg-amber-50 text-amber-700 border-amber-200 cursor-default'}`}
                                    title={canRecover ? `Click to recover lost pairs from ${sh.stage_name}` : `Loss at ${sh.stage_name} (admin/manager can recover)`}
                                  >
                                    -{sh.loss_qty} {sh.stage_name}{canRecover ? ' ↻' : ''}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-center font-mono text-xs">{item.size}</td>
                          <td className="p-2"><Input type="number" value={item.input_qty} onChange={e => updateBatchItem(idx, 'input_qty', e.target.value)} className="h-7 text-xs font-mono text-right rounded-sm" data-testid={`input-${item.sku}-${item.size}`} /></td>
                          <td className="p-2"><Input type="number" value={item.output_qty} onChange={e => updateBatchItem(idx, 'output_qty', e.target.value)} className="h-7 text-xs font-mono text-right rounded-sm" data-testid={`output-${item.sku}-${item.size}`} /></td>
                          <td className="p-2 text-right">{item.input_qty && item.output_qty ? <span className={`font-mono text-xs font-bold ${loss > 0 ? 'text-red-600' : 'text-green-600'}`}>{loss > 0 ? `-${loss}` : '0'}</span> : <span className="text-xs text-zinc-300">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-zinc-50">
                    <tr className="border-t border-zinc-300">
                      <td colSpan={2} className="p-2 text-xs font-bold">Total</td>
                      <td className="p-2 text-right font-mono text-xs font-bold">{batchForm.items.reduce((s, i) => s + (parseInt(i.input_qty) || 0), 0)}</td>
                      <td className="p-2 text-right font-mono text-xs font-bold">{batchForm.items.reduce((s, i) => s + (parseInt(i.output_qty) || 0), 0)}</td>
                      <td className="p-2 text-right font-mono text-xs font-bold text-red-600">{(() => { const t = batchForm.items.reduce((s, i) => s + ((parseInt(i.input_qty) || 0) - (parseInt(i.output_qty) || 0)), 0); return t > 0 ? `-${t}` : '0'; })()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-zinc-200">
              <Button variant="outline" className="h-9 rounded-sm" onClick={() => setEntryModal(false)}>Cancel</Button>
              <Button className="h-9 rounded-sm bg-[#09090B]" onClick={submitBatchEntry} disabled={loading} data-testid="submit-production-entry">
                {loading ? 'Saving...' : `Save ${batchForm.items.length} Entries`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recover Shortage Modal */}
      <Dialog open={!!recovering} onOpenChange={(o) => { if (!o) { setRecovering(null); setRecoverQty(''); } }}>
        <DialogContent className="max-w-md" data-testid="recover-shortage-dialog">
          <DialogHeader>
            <DialogTitle>Recover Lost Pairs</DialogTitle>
          </DialogHeader>
          {recovering && (
            <div className="space-y-3 mt-2">
              <div className="text-xs text-zinc-600">
                <div><span className="text-zinc-500">SKU:</span> <span className="font-mono">{recovering.sku} (Size {recovering.size})</span></div>
                <div><span className="text-zinc-500">Stage:</span> <span className="font-mono">{recovering.stage_name}</span></div>
                <div><span className="text-zinc-500">Lost pairs available to recover:</span> <span className="font-mono font-bold text-amber-700">{recovering.max_qty}</span></div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Quantity to Recover</Label>
                <Input
                  type="number" min="1" max={recovering.max_qty}
                  value={recoverQty} onChange={(e) => setRecoverQty(e.target.value)}
                  className="mt-1 h-9 rounded-sm font-mono"
                  data-testid="recover-qty-input"
                  autoFocus
                />
              </div>
              <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                Recovered pairs will move from <strong>loss</strong> to <strong>output</strong> at {recovering.stage_name}.
                Wages and penalty will be recalculated. If later stages were already entered, you may need to top them up manually.
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                <Button variant="outline" className="h-8 rounded-sm" onClick={() => { setRecovering(null); setRecoverQty(''); }}>Cancel</Button>
                <Button className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitRecover} data-testid="submit-recover">Recover</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
